/**
 * useTutorTracking — manages AI Tutor session lifecycle tracking.
 *
 * Design principles:
 * - All session state lives in refs (no re-renders triggered).
 * - All tracking calls are best-effort: failures never throw or toast.
 * - Tutor UI must remain fully functional even if all tracking calls fail.
 * - Ping uses `ts` field (backend OpenAPI requirement, not `timestamp`).
 * - sendBeacon on unload is best-effort only — it cannot attach Authorization
 *   headers, so the backend may reject it. Ping is the reliability mechanism.
 */

import { useEffect, useRef } from 'react';
import {
  startTutorSession,
  pingTutorSession,
  submitTutorSummary,
  getMyTutorSummary,
  tutorTrackingAbsolute,
} from '../api/tutorTrackingApi';
import type { TranscriptTurn, TutorSummaryOut, SessionSummaryIn } from '../types/tracking';

const PING_INTERVAL_MS = 50_000;
const MAX_TRANSCRIPT_TURNS = 200;

export function useTutorTracking() {
  const sessionIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<string | null>(null);
  const transcriptRef = useRef<TranscriptTurn[]>([]);
  const pingIntervalRef = useRef<number | null>(null);
  const summarySubmittedRef = useRef<boolean>(false);
  const modeRef = useRef<string>('VOICE');
  const tutorTypeRef = useRef<string>('FREE');
  const lessonNoRef = useRef<number | undefined>(undefined);
  const sublessonNoRef = useRef<string | undefined>(undefined);
  const topicLabelRef = useRef<string | undefined>(undefined);

  function buildSummaryPayload(): SessionSummaryIn | null {
    if (!sessionIdRef.current || !startedAtRef.current) return null;
    return {
      session_id: sessionIdRef.current,
      started_at: startedAtRef.current,
      ended_at: new Date().toISOString(),
      mode: modeRef.current,
      tutor_type: tutorTypeRef.current,
      lesson_no: lessonNoRef.current,
      sublesson_no: sublessonNoRef.current,
      topic_label: topicLabelRef.current,
      transcript: [...transcriptRef.current],
    };
  }

  function stopPing() {
    if (pingIntervalRef.current !== null) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }

  /**
   * Call when session starts (after Gemini Live onopen fires).
   * tutorType: "FREE" | "TOPIC" — never pass "LESSON" unless lessonNo is valid.
   */
  async function beginSession(opts: {
    mode: string;
    tutorType: string;
    lessonNo?: number;
    sublessonNo?: string;
    topicLabel?: string;
  }): Promise<void> {
    // Reset state for new session
    sessionIdRef.current = null;
    startedAtRef.current = null;
    transcriptRef.current = [];
    summarySubmittedRef.current = false;
    stopPing();

    modeRef.current = opts.mode;
    tutorTypeRef.current = opts.tutorType;
    lessonNoRef.current = opts.lessonNo;
    sublessonNoRef.current = opts.sublessonNo;
    topicLabelRef.current = opts.topicLabel;

    const result = await startTutorSession({
      mode: opts.mode as 'VOICE' | 'TEXT',
      tutor_type: opts.tutorType as 'FREE' | 'TOPIC' | 'LESSON',
      lesson_no: opts.lessonNo,
      sublesson_no: opts.sublessonNo,
      topic_label: opts.topicLabel,
    });

    if (!result) {
      // Tracking disabled for this session — tutor continues normally
      console.warn('[useTutorTracking] startSession failed; tracking disabled for this session');
      return;
    }

    sessionIdRef.current = result.session_id;
    startedAtRef.current = result.started_at;

    // Start ping interval — uses `ts` field per backend spec
    pingIntervalRef.current = window.setInterval(() => {
      if (!sessionIdRef.current) return;
      pingTutorSession({
        session_id: sessionIdRef.current,
        ts: new Date().toISOString(),
        mode: modeRef.current,
        tutor_type: tutorTypeRef.current,
      });
    }, PING_INTERVAL_MS);
  }

  /**
   * Append a transcript turn. Called when Gemini Live returns text parts
   * or when user text is available. Capped at MAX_TRANSCRIPT_TURNS.
   */
  function recordTurn(role: 'user' | 'model', text: string): void {
    if (!text.trim()) return;
    transcriptRef.current.push({ role, text: text.trim() });
    if (transcriptRef.current.length > MAX_TRANSCRIPT_TURNS) {
      transcriptRef.current.shift();
    }
  }

  /**
   * Call on session end (disconnect button, End Class button, or unmount).
   * Idempotent — safe to call multiple times.
   */
  function endSession(): void {
    if (summarySubmittedRef.current) return;
    if (!sessionIdRef.current) return;

    summarySubmittedRef.current = true;
    stopPing();

    const payload = buildSummaryPayload();
    if (payload) {
      submitTutorSummary(payload);
    }
  }

  async function loadMySummary(days = 30): Promise<TutorSummaryOut | null> {
    return getMyTutorSummary(days);
  }

  // Unload + visibility safeguards
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopPing();

      if (!sessionIdRef.current || summarySubmittedRef.current) return;
      summarySubmittedRef.current = true;

      const payload = buildSummaryPayload();
      if (!payload) return;

      // sendBeacon cannot attach Authorization headers — this is best-effort only.
      // The backend may reject with 401; that is acceptable. Engagement reliability
      // comes from the 50s ping interval (which sends full auth headers).
      try {
        navigator.sendBeacon(
          tutorTrackingAbsolute('/session/summary'),
          new Blob([JSON.stringify(payload)], { type: 'application/json' })
        );
      } catch {
        // silent — sendBeacon may not be available in all environments
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      if (!sessionIdRef.current) return;

      // Fire one immediate ping to record last_active before tab hides
      pingTutorSession({
        session_id: sessionIdRef.current,
        ts: new Date().toISOString(),
        mode: modeRef.current,
        tutor_type: tutorTypeRef.current,
      }).catch(() => {});
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      endSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getSessionId(): string | null {
    return sessionIdRef.current;
  }

  return { beginSession, recordTurn, endSession, loadMySummary, getSessionId };
}
